// Where a Pending Approvals card opens when it is clicked (pure, so it can be tested without rendering anything).
//
// Each card opens the app's own approval screen where one exists ("we already have those UI, show that not the new
// one" - the product owner, 2026-10-01). A card whose screen the viewer cannot open falls back to this dashboard's own
// read-only table for that type, never to a 404.
//
//  - Leave RH: a reporting head / HOD opens the app's own reporting head leave page (hod-leave-applications, the
//    "department leave" page in their sidebar). HR sees every team's requests, which that page never shows (it is the
//    viewer's own team, and it is closed to anyone who is not a head), so HR - and anyone else - opens the dashboard's
//    own "Department Leave Approvals" page instead.
//  - Leave HR: the dashboard's "Department Leave Approvals" page (HR stage). It lists exactly the requests the card
//    counted (not limited to a month) and each row opens the standard approve screen.
//  The Leave cards used to open the WFH GRANT pages, which are a different request type (leaveapp rows with a
//  negative leave type).
//  - WFH grant (RH / HR): the real grant approval pages. HR's page needs the 'wfh grant approval' permission.

export const DEPARTMENT_LEAVE_PAGE = '/dashboard/warning-dashboard/department-leave-approvals';
export const RH_LEAVE_PAGE = '/my-workspace/reporting_head_or_hod/hod-leave-applications';
export const RH_GRANT_PAGE = '/my-workspace/hod-leave-grant-requests';
export const HR_GRANT_PAGE = '/my-workspace/hr-leave-grant-requests';
export const HR_GRANT_PERMISSION = 'wfh grant approval';

export const RH_REGULARIZATION_PAGE = '/my-workspace/attendance-regularization';

const ownTable = (key) => `/dashboard/warning-dashboard/pending-approvals?type=${key}`;

// 'YYYY-MM-DD' in the viewer's local time (the server stores and compares dates in IST too).
const localDay = (d) => {
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
};

// The regularization page lists requests by creation date and, with no dates given, shows only the current month -
// while the card counts every pending request, however old. So open it on the range the card counted: from the oldest
// pending request (its level-1 approval is created together with the request) to today.
const regularizationRange = (items = [], now = new Date()) => {
  const days = items.map((i) => localDay(i.reg_approval_created_at)).filter(Boolean).sort();
  if (days.length === 0) return '';
  const today = localDay(now);
  return `?fromDate=${days[0]}&toDate=${days[days.length - 1] > today ? days[days.length - 1] : today}`;
};

/**
 * @param key     the approval card key from GET warning-dashboard/pending-approvals-dashboard
 * @param viewer  { isHr, isReportingHead, navChild } - HR department?, a reporting head / HOD?, the nav permissions held
 * @param row     the card's row from that same answer (its items), when the target needs it
 */
export const approvalTarget = (key, { isHr = false, isReportingHead = false, navChild = [] } = {}, row = null) => {
  const canOpenHrGrant = navChild.includes(HR_GRANT_PERMISSION);
  switch (key) {
    case 'leave-rh':
      // HR's count is org-wide, the head page shows only the viewer's own team - so HR never goes there
      return !isHr && isReportingHead ? RH_LEAVE_PAGE : `${DEPARTMENT_LEAVE_PAGE}?stage=rh`;
    case 'leave-hr':
      return `${DEPARTMENT_LEAVE_PAGE}?stage=hr`;
    case 'wfh-grant-rh':
      // a reporting head works their own team's requests on the RH page; HR (who sees every request) works them on HR's page
      if (!isHr) return RH_GRANT_PAGE;
      return canOpenHrGrant ? HR_GRANT_PAGE : ownTable(key);
    case 'wfh-grant-hr':
      return canOpenHrGrant ? HR_GRANT_PAGE : ownTable(key);
    case 'regularization-rh':
      // same rule as Leave RH: HR's count is org-wide, the head page shows only the viewer's own team
      if (isHr || !isReportingHead) return ownTable(key);
      return `${RH_REGULARIZATION_PAGE}${regularizationRange(row?.items)}`;
    case 'compensatory':
      return '/my-workspace/reporting-head-comp-work-req';
    default:
      // 'regularization-hr' has no live HR screen (AttendanceRegularization.jsx supports head="HR" but no route passes it)
      return ownTable(key);
  }
};

// The approve screen a row of the Department Leave Approvals page opens. HR approves any request; a reporting head /
// HOD approves their own team's (the screen itself re-checks that on the server).
export const leaveApproveRoute = (leaveId, stage, { isHr = false } = {}) =>
  stage === 'hr' || isHr
    ? `/my-workspace/hr-leave-approve/${leaveId}`
    : `/my-workspace/reporting_head_or_hod/hod-leave-approve/${leaveId}`;

// The viewer facts the rules above need, read the way the rest of this dashboard does (saved login profile).
export const readViewer = () => {
  try {
    const profile = JSON.parse(localStorage.getItem('cred'))?.profile || {};
    return { isHr: profile.departmentName === 'Human Resource (HR)', isReportingHead: Boolean(profile.isHod || profile.isReportingHead) };
  } catch {
    return { isHr: false, isReportingHead: false };
  }
};
